import {
  boolean,
  uuid,
  text,
  doublePrecision,
  bigint,
  index,
  pgTable,
  timestamp,
  varchar,
  foreignKey,
  integer,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { identity_user } from '@trashlab/api/src/domains/identity/schemas/identity-user';
import { hauler_location } from '@trashlab/api/src/domains/identity/schemas/hauler-location';
import { orders } from '@trashlab/api/src/domains/orders/schemas/orders';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';

export const driver = pgTable(
  'driver',
  {
    ...defaultFields.fieldsWithTenantId(),
    // Nullable to allow temporary NULL during primary user swaps (unique constraint requires this)
    // IMPORTANT: This should NEVER be NULL at the end of any transaction
    identityUserId: uuid('identityUserId').unique(),
    /** Schedule name - required for new schedules, backfilled from user name for existing */
    name: varchar('name', { length: 255 }),
    routeColor: text('routeColor'),
    lastModifiedBy: uuid('lastModifiedBy'),
    orderingIndex: doublePrecision('orderingIndex')
      .default(sql`EXTRACT(epoch FROM now())`)
      .notNull(),
    createdBy: uuid('createdBy'),
    isDisplayedPreference: boolean('isDisplayedPreference')
      .default(true)
      .notNull(),
    enableRouteOptimization: boolean('enableRouteOptimization')
      .default(false)
      .notNull(),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    eligibleForDispatch: boolean('eligibleForDispatch').default(true).notNull(),
    branchId: uuid('branchId'),
  },
  (table) => {
    return {
      mobileDispatchActive: index('driver_mobile_dispatch_active_idx').on(table.tenantId, table.id).where(sql`${table.archivedAtTs} IS NULL AND ${table.eligibleForDispatch} = true`),
      createdAtTs_idx: index('driver_createdAtTs_idx').on(table.createdAtTs),
      tenantId_idx: index('driver_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('driver_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx1: index('driver_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('driver_versionNumber_idx').on(
        table.versionNumber,
      ),
      identityUserId_idx: index('driver_identityUserId_idx').on(
        table.identityUserId,
      ),
      branchId_idx: index('driver_branchId_idx').on(table.branchId),
      
      // Composite index for efficient driver lookups by tenantId and identityUserId
      tenant_identity_idx: index('idx_driver_tenant_identity').on(
        table.tenantId,
        table.identityUserId
      ),

      driver_identityUserId_fkey: foreignKey({
        columns: [table.identityUserId],
        foreignColumns: [identity_user.id],
        name: 'driver_identityUserId_fkey',
      }),
      driver_branchId_fkey: foreignKey({
        columns: [table.branchId],
        foreignColumns: [hauler_location.id],
        name: 'driver_branchId_fkey',
      }),
    };
  },
);

export const driver_history = pgTable('driver_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  identityUserId: uuid('identityUserId'),
  name: varchar('name', { length: 255 }),
  routeColor: text('routeColor'),
  lastModifiedBy: uuid('lastModifiedBy'),
  orderingIndex: doublePrecision('orderingIndex'),
  createdBy: uuid('createdBy'),
  isDisplayedPreference: boolean('isDisplayedPreference'),
  enableRouteOptimization: boolean('enableRouteOptimization'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  eligibleForDispatch: boolean('eligibleForDispatch'),
  branchId: uuid('branchId'),
});

export const additional_drivers = pgTable(
  'additional_drivers',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    orderId: uuid('orderId').notNull(),
    driverId: uuid('driverId').notNull().unique(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('additional_drivers_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('additional_drivers_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('additional_drivers_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('additional_drivers_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('additional_drivers_versionNumber_idx').on(
        table.versionNumber,
      ),
      driverId_idx: index('additional_drivers_driverId_idx').on(table.driverId),
      orderId_idx: index('additional_drivers_orderId_idx').on(table.orderId),
      
      // Composite index for efficient additional driver lookups
      tenant_driver_idx: index('idx_additional_drivers_tenant_driver').on(
        table.tenantId,
        table.driverId
      ),

      //foreign keys
      additional_drivers_driverId_fkey: foreignKey({
        columns: [table.driverId],
        foreignColumns: [driver.id],
        name: 'additional_drivers_driverId_fkey',
      }),
      additional_drivers_orderId_fkey: foreignKey({
        columns: [table.orderId],
        foreignColumns: [orders.id],
        name: 'additional_drivers_orderId_fkey',
      }),
    };
  },
);

export const additional_drivers_history = pgTable(
  'additional_drivers_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    orderId: uuid('orderId'),
    driverId: uuid('driverId'),
  },
);

export const driver_route_sequence = pgTable(
  'driver_route_sequence',
  {
    ...defaultFields.fieldsWithTenantId(),
    namespace: varchar('namespace', { length: 255 }).notNull(),
    counter: integer('counter').default(1).notNull(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
  },
  (table) => {
    return {
      createdAtTs_idx: index('driver_route_sequence_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('driver_route_sequence_tenantId_idx').on(
        table.tenantId,
      ),
      updatedAtTs_idx: index('driver_route_sequence_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('driver_route_sequence_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('driver_route_sequence_versionNumber_idx').on(
        table.versionNumber,
      ),
    };
  },
);

export const driver_route_sequence_history = pgTable(
  'driver_route_sequence_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    namespace: varchar('namespace', { length: 255 }),
    counter: integer('counter'),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
  },
);

export type SelectDriver = typeof driver.$inferSelect;
